/**
 * LeadListView — the dense tabular Lens (AD-046 §7.2). Columns: Lead ·
 * Source (folded 5-source) · Pipeline chip · Current stage (N of M) ·
 * Service · Value (role-gated) · Age. Source + state filters, Group
 * None|Source, client-side sort, and the new client-side Leads SEARCH box
 * (locked L-7). Reads only the in-memory `leads` array — zero new Firestore.
 */
import React, { useMemo, useState } from 'react';
import {
  LEAD_STATE_LABEL,
  leadAgeDays,
  type Lead,
  type LeadState,
} from '../../lib/leads';
import {
  TARGET_SOURCES,
  groupBySource,
  leadMatchesSearch,
  leadValue,
  sortLeads,
  sourceColour,
  sourceMap,
  spineProgress,
  type LeadSortKey,
  fstageProgress,
  type TargetSource,
} from '../../lib/leadLenses';
import { pipelineOf, L0_PRODUCT_LABEL } from '../../lib/leadPipeline';
import { inr } from '../../lib/trackDemo';

export default function LeadListView({
  leads,
  onSelect,
  selectedId,
  seesMoney,
  nowISO,
}: {
  leads: Lead[];
  onSelect: (id: string) => void;
  selectedId: string | null;
  seesMoney: boolean;
  nowISO: string;
}) {
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState<TargetSource | 'all'>('all');
  const [stateFilter, setStateFilter] = useState<LeadState | 'all'>('all');
  const [group, setGroup] = useState<'none' | 'source'>('none');
  const [sortKey, setSortKey] = useState<LeadSortKey>('age');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const filtered = useMemo(() => {
    let out = leads.filter((l) => leadMatchesSearch(l, search));
    if (sourceFilter !== 'all') out = out.filter((l) => sourceMap(l.source) === sourceFilter);
    if (stateFilter !== 'all') out = out.filter((l) => l.state === stateFilter);
    return sortLeads(out, sortKey, sortDir, nowISO);
  }, [leads, search, sourceFilter, stateFilter, sortKey, sortDir, nowISO]);

  const grouped = useMemo(
    () => (group === 'source' ? groupBySource(filtered) : null),
    [group, filtered],
  );

  function Row({ l }: { l: Lead }) {
    const pipe = pipelineOf(l);
    const fprog = pipe === 'fiverr' ? fstageProgress(l) : null;
    const prog = fprog ?? spineProgress(l.state);
    const stageLabel: string = fprog ? fprog.label : LEAD_STATE_LABEL[l.state];
    const val = leadValue(l);
    const selected = l.id === selectedId;
    return (
      <tr
        data-lead-id={l.id}
        onClick={() => onSelect(l.id)}
        onKeyDown={(e) => {
          // Keyboard parity with the mouse click (a11y): Enter/Space opens.
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelect(l.id);
          }
        }}
        tabIndex={0}
        role="button"
        aria-label={`Open lead ${l.name}`}
        aria-current={selected ? true : undefined}
        style={{
          cursor: 'pointer',
          // Selected: accent wash + a 2px accent ring (outline, so border-collapse
          // stays intact) + soft accent elevation, matching `.card.selected`.
          background: selected ? 'var(--accent-wash, #E9F7EF)' : undefined,
          outline: selected ? '2px solid var(--accent, #008069)' : undefined,
          outlineOffset: selected ? '-2px' : undefined,
          boxShadow: selected ? '0 8px 22px -10px rgba(0,128,105,.55)' : undefined,
        }}
      >
        <td style={tdName}>
          <div style={{ fontWeight: 700 }}>
            {l.name}
            {l.consultationPaid && (
              <span style={{ ...chip, marginLeft: 6, color: 'var(--info, #2563EB)', borderColor: '#C7DBF7', background: 'var(--info-wash, #EFF6FF)' }}>
                ₹599 consult ✓
              </span>
            )}
          </div>
          <div style={{ fontSize: 10, color: 'var(--ink-mute, #6B7280)' }}>{l.id}</div>
        </td>
        <td style={td}>
          <span style={{ ...chip, color: sourceColour(l.source), borderColor: sourceColour(l.source) }}>
            {sourceMap(l.source)}
          </span>
          {l.l0Product && (
            <div style={{ fontSize: 9, color: 'var(--ink-mute, #6B7280)', marginTop: 2 }}>
              L0 · {L0_PRODUCT_LABEL[l.l0Product]}
            </div>
          )}
        </td>
        <td style={td}>
          <span
            style={{
              ...chip,
              color: pipe === 'fiverr' ? 'var(--ai, #6D28D9)' : 'var(--ink-soft, #4B5563)',
              borderColor: pipe === 'fiverr' ? '#D9CFF5' : 'var(--rule, #E5E7EB)',
            }}
          >
            {pipe === 'fiverr' ? 'Fiverr' : 'Standard'}
          </span>
        </td>
        <td style={td}>
          {stageLabel}
          <span style={{ color: 'var(--ink-mute, #6B7280)', fontSize: 10 }}>
            {' '}· {prog.n} of {prog.m}
          </span>
        </td>
        <td style={td}>{l.assignedTier ?? '—'}</td>
        <td style={{ ...td, textAlign: 'right' }}>
          {seesMoney ? (val > 0 ? inr(val) : '—') : '•••'}
        </td>
        <td style={{ ...td, textAlign: 'right' }}>{leadAgeDays(l, nowISO)}d</td>
      </tr>
    );
  }

  return (
    <div style={{ padding: 12 }}>
      {/* controls */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 10 }}>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search leads…"
          aria-label="Search leads"
          style={{
            flex: '1 1 200px',
            minWidth: 160,
            padding: '6px 10px',
            border: '1px solid var(--rule-strong, #D1D5DB)',
            borderRadius: 'var(--r-pill, 999px)',
            fontSize: 12.5,
          }}
        />
        <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value as TargetSource | 'all')} style={sel} aria-label="Filter by source">
          <option value="all">All sources</option>
          {TARGET_SOURCES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <select value={stateFilter} onChange={(e) => setStateFilter(e.target.value as LeadState | 'all')} style={sel} aria-label="Filter by stage">
          <option value="all">All stages</option>
          {(Object.keys(LEAD_STATE_LABEL) as LeadState[]).map((s) => (
            <option key={s} value={s}>{LEAD_STATE_LABEL[s]}</option>
          ))}
        </select>
        <select value={group} onChange={(e) => setGroup(e.target.value as 'none' | 'source')} style={sel} aria-label="Group">
          <option value="none">Group: None</option>
          <option value="source">Group: Source</option>
        </select>
        <select
          value={`${sortKey}:${sortDir}`}
          onChange={(e) => {
            const [k, d] = e.target.value.split(':');
            setSortKey(k as LeadSortKey);
            setSortDir(d as 'asc' | 'desc');
          }}
          style={sel}
          aria-label="Sort"
        >
          <option value="age:desc">Oldest first</option>
          <option value="age:asc">Newest first</option>
          <option value="value:desc">Value high→low</option>
          <option value="value:asc">Value low→high</option>
          <option value="name:asc">Name A→Z</option>
        </select>
        <span style={{ fontSize: 11, color: 'var(--ink-mute, #6B7280)' }}>{filtered.length} shown</span>
      </div>

      {filtered.length === 0 ? (
        <div style={empty}>No leads match.</div>
      ) : group === 'source' && grouped ? (
        TARGET_SOURCES.filter((s) => grouped[s].length > 0).map((s) => (
          <div key={s} style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: sourceColour(grouped[s][0].source), margin: '6px 2px' }}>
              {s} · {grouped[s].length}
            </div>
            <Table>{grouped[s].map((l) => <Row key={l.id} l={l} />)}</Table>
          </div>
        ))
      ) : (
        <Table>{filtered.map((l) => <Row key={l.id} l={l} />)}</Table>
      )}
    </div>
  );
}

function Table({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ overflowX: 'auto', border: '1px solid var(--rule, #E5E7EB)', borderRadius: 'var(--r-md, 13px)' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr>
            {['Lead', 'Source', 'Pipeline', 'Stage', 'Service', 'Value', 'Age'].map((h, i) => (
              <th key={h} style={{ ...th, textAlign: i >= 5 ? 'right' : 'left' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

const th: React.CSSProperties = {
  background: 'var(--inset, #F6F7F9)',
  color: 'var(--ink-mute, #6B7280)',
  fontWeight: 700,
  textTransform: 'uppercase',
  fontSize: 9.5,
  letterSpacing: '.04em',
  padding: '6px 9px',
  borderBottom: '1px solid var(--rule, #E5E7EB)',
};
const td: React.CSSProperties = { padding: '7px 9px', borderBottom: '1px solid var(--rule, #E5E7EB)', verticalAlign: 'top' };
const tdName: React.CSSProperties = { ...td, minWidth: 120 };
const chip: React.CSSProperties = {
  display: 'inline-block',
  padding: '1px 8px',
  borderRadius: 'var(--r-pill, 999px)',
  fontSize: 9.5,
  fontWeight: 700,
  border: '1px solid var(--rule, #E5E7EB)',
};
const sel: React.CSSProperties = {
  padding: '5px 8px',
  border: '1px solid var(--rule-strong, #D1D5DB)',
  borderRadius: 'var(--r-sm, 9px)',
  fontSize: 12,
  background: 'var(--paper, #fff)',
};
const empty: React.CSSProperties = {
  padding: '30px 12px',
  textAlign: 'center',
  color: 'var(--ink-mute, #6B7280)',
  fontSize: 13,
};
